/* eslint-disable jsx-a11y/no-static-element-interactions */
import {
  Check,
  ChevronDown,
} from "@liftedcare/solidjs-untitled-icons/duocolor";
import { SearchSm } from "@liftedcare/solidjs-untitled-icons/line";
import {
  Component,
  createEffect,
  createMemo,
  createSignal,
  createUniqueId,
  For,
  JSX,
  mergeProps,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { unwrap } from "solid-js/store";
import { Portal } from "solid-js/web";

import { focusedWithinComponent } from "../../utils/focusedWithinElement.js";
import { onKeyDown } from "../../utils/inputUtils.js";
import { Pill } from "../common/Pill.js";
import { IssueDisplay, useForm } from "./FormContext.js";
import { ISelectOption, SelectValueType } from "./Select.js";

interface ComboboxProps<T> {
  value?: T[];
  editable?: boolean;
  name?: string;
  class?: string;
  listContainerClass?: string;
  labelClass?: string;
  label?: string | JSX.Element;
  options: ISelectOption[];
  onChange?: (value: T[] | T) => void;
  error?: string;
  tabIndex?: number;
  placeholder?: string;
  isSingle?: boolean;
  maxDisplayValues?: number;
  disabled?: boolean;
  hideValidation?: boolean;
  onChangeOptionsVisibility?: (opened: boolean) => void;
  displayType?: "text" | "pill";
  classList?: JSX.CustomAttributes<HTMLDivElement>["classList"];
}

function Combobox<
  ValueType extends SelectValueType & Component<ComboboxProps<ValueType>>,
>(unmergedProps: ComboboxProps<ValueType>) {
  const props = mergeProps({ tabIndex: 0 }, unmergedProps);

  const [opened, setOpened] = createSignal(false);
  const [activeOptionIndex, setActiveOptionIndex] = createSignal<number>(-1);
  const [focused, setFocused] = createSignal(false);

  const [filterQuery, setFilterQuery] = createSignal("");

  const commonClassName = createMemo(
    () => `border rounded-md ${props.editable ? "p-2" : ""} ${props.class}`,
  );
  const listContainerClassName = createMemo(
    () => `${props.listContainerClass}`,
  );
  const id = createUniqueId();
  const componentId = createUniqueId();
  const menuElmId = createUniqueId();

  let searchBarElm!: HTMLInputElement;
  let containerDiv!: HTMLDivElement;
  let menuElm!: HTMLDivElement;

  const formContext = useForm();

  const selectedValues = () => {
    if (formContext && props.name) {
      if (
        formContext.getData(props.name) !== null &&
        !Array.isArray(formContext.getData(props.name))
      ) {
        return [formContext.getData(props.name)];
      }
      return (formContext.getData(props.name) as any[]) ?? [];
    }
    return props.value ?? [];
  };

  const toggleSelect = () => {
    if (props.disabled) {
      return;
    }
    if (!opened()) {
      setFilterQuery("");
    }
    setOpened(!opened());

    // set focus on the search box or back on the container
    setTimeout(() => {
      if (opened()) {
        menuElm.style.top = `${containerDiv.getBoundingClientRect().top}px`;
        menuElm.style.left = `${containerDiv.getBoundingClientRect().left}px`;
        menuElm.style.width = `${containerDiv.getBoundingClientRect().width}px`;
        menuElm.style.position = "absolute";

        // this prevents screen jank as the menu position is calculated
        menuElm.style.display = "block";

        setTimeout(() => {
          searchBarElm?.focus();
        }, 1);
      } else {
        containerDiv?.focus();
      }
    }, 1);
  };

  const valueChanged = props.isSingle
    ? (value: ValueType) => {
        if (formContext && props.name) {
          formContext.setData(props.name, value);
        }
        props.onChange?.(value);
      }
    : (value: ValueType[]) => {
        let newValues: any[] = unwrap(selectedValues());
        newValues = newValues.filter((v) => v !== undefined);
        if (newValues.includes(value)) {
          newValues = newValues.filter((storedValue) => storedValue !== value);
        } else {
          newValues.push(value);
        }
        if (formContext && props.name) {
          formContext.setData(props.name, newValues);
        }
        props.onChange?.(newValues);
      };

  const onSelectAllClick = () => {
    const allValues = props.options.map((option) => option.value);
    if (formContext && props.name) {
      formContext.setData(props.name, allValues);
    }
    props.onChange?.(allValues);
  };

  const onClearClick = () => {
    if (formContext && props.name) {
      formContext.setData(props.name, []);
    }
    props.onChange?.([]);
  };

  const filteredOptions = () => {
    if (!filterQuery().length) {
      return props.options;
    }
    return props.options.filter((option) =>
      option.name.toLowerCase().includes(filterQuery().toLowerCase()),
    );
  };

  const keyboardEventHandler = (event: KeyboardEvent) => {
    if (event.defaultPrevented) {
      return;
    }

    if (
      !focusedWithinComponent(componentId, document.activeElement ?? event) &&
      event.target !== searchBarElm
    ) {
      setOpened(false);
      setFocused(false);
      return;
    }

    if (opened()) {
      if (event.key === "ArrowDown") {
        setActiveOptionIndex(
          Math.min(activeOptionIndex() + 1, props.options.length - 1),
        );
      } else if (event.key === "ArrowUp") {
        setActiveOptionIndex(Math.max(activeOptionIndex() - 1, 0));
      }
    }
    if (focused()) {
      if ([" ", "Enter"].includes(event.key)) {
        if (opened()) {
          const value = filteredOptions().at(activeOptionIndex())?.value;
          if (value) {
            valueChanged(value);
          }
        } else {
          setOpened(true);
        }
      }
      if (event.key === "Escape" && opened()) {
        setOpened(false);
        setFocused(false);
      }
    }
  };

  createEffect(() => {
    props.onChangeOptionsVisibility?.(opened());
  });

  const mouseClickEventHandler = async (event: MouseEvent) => {
    if (event.defaultPrevented) {
      return;
    }
    // Event is fired and handled before focus change so to let the tab button switch focus to other component, delay handling in event loop
    await new Promise((r) => setTimeout(r, 1));

    if (
      (focused() || opened()) &&
      !focusedWithinComponent(menuElmId, event ?? document.activeElement) &&
      !focusedWithinComponent(menuElmId, event ?? menuElm) &&
      event.target !== searchBarElm
    ) {
      setOpened(false);
      setFocused(false);
      return;
    }
  };

  onMount(() => {
    document.addEventListener("click", mouseClickEventHandler);
    document.addEventListener("keydown", keyboardEventHandler);
  });

  onCleanup(() => {
    document.removeEventListener("click", mouseClickEventHandler);
    document.removeEventListener("keydown", keyboardEventHandler);
  });

  const getFormErrors = () => {
    if (!props.name) {
      return false;
    }

    const errors = formContext?.getErrors(props.name);
    // sometimes this returns true, which means the field is valid
    if (errors && errors !== true) {
      return errors.filter((a: any) => Boolean(a) && a.length);
    }
    return formContext?.errors?.()?.[props.name] ?? false;
  };

  const relevantIssues = () => formContext?.getIssues(props.name);

  const classList = () => props.classList ?? {};

  return (
    <div class="relative flex w-full flex-col" id={componentId}>
      <Show when={props.label}>
        <label for={id} class={props.labelClass}>
          {props.label}
        </label>
      </Show>
      <Show
        when={props.editable || props.editable === undefined}
        fallback={
          <div
            class={`border-transparent ${commonClassName()}`}
            classList={{ "cursor-pointer": props.editable, ...classList() }}
          >
            <ListSelectedOptions
              displayType={props.displayType}
              options={props.options}
              values={unwrap(selectedValues())}
              placeholder={props.placeholder}
              showAll={!props.editable}
            />
          </div>
        }
      >
        {/* TODO: a11y */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
        <div
          ref={containerDiv!}
          class={`cursor-pointer ${commonClassName()}`}
          classList={{
            "border-error-500 outline-error-500":
              Boolean(getFormErrors()?.length) ||
              Boolean(relevantIssues()?.length),
            ...classList(),
          }}
          onClick={toggleSelect}
          tabIndex={props.tabIndex}
          onFocus={() => {
            setFocused(true);
          }}
        >
          <button
            onClick={(e) => e.preventDefault()}
            class={`flex h-6 w-full items-center justify-between`}
          >
            <div class="flex w-[calc(100%-2rem)] items-center gap-2 overflow-hidden">
              <Show when={unwrap(selectedValues()).length === 0}>
                <span class="text-gray-500">{props.placeholder}</span>
              </Show>
              <For
                each={props.options
                  .filter((option) =>
                    unwrap(selectedValues()).includes(option.value),
                  )
                  .slice(0, props.maxDisplayValues ?? 2)}
              >
                {(option) => {
                  return (
                    <div class="truncate rounded-full bg-primary-100 px-3 py-1 text-sm">
                      {option.name}
                    </div>
                  );
                }}
              </For>
              <Show
                when={
                  unwrap(selectedValues()).length >
                  (props.maxDisplayValues ?? 2)
                }
              >
                <div>
                  <div class="whitespace-nowrap rounded-full border bg-primary-100 px-2">
                    <span class="inline-block">+</span>
                    <span class="inline-block">
                      {unwrap(selectedValues()).length -
                        (props.maxDisplayValues ?? 2)}
                    </span>
                  </div>
                </div>
              </Show>
            </div>
            <div
              class="transition duration-300"
              classList={{
                "rotate-180": opened(),
              }}
            >
              <ChevronDown class="size-5" />
            </div>
          </button>
        </div>
        <Show when={opened()}>
          <Portal>
            <div
              class={`absolute z-50 hidden max-h-[50svh] divide-y overflow-y-auto rounded-md border bg-white shadow-md ${listContainerClassName()}`}
              ref={menuElm!}
              id={menuElmId}
            >
              <div class="flex flex-row items-center px-2">
                <SearchSm class="size-5" />
                <input
                  class="mx-2 w-full py-2 outline-none"
                  onKeyDown={onKeyDown}
                  onInput={(e) => setFilterQuery(e.currentTarget.value)}
                  ref={searchBarElm!}
                />
              </div>
              <Show when={!props.isSingle}>
                <div class="flex items-center justify-end gap-2 p-2 text-sm">
                  <button
                    class="text-blue-500 underline underline-offset-2"
                    onClick={onSelectAllClick}
                  >
                    Select all
                  </button>
                  <button
                    class="text-blue-500 underline underline-offset-2"
                    onClick={onClearClick}
                  >
                    Clear
                  </button>
                </div>
              </Show>
              <For each={filteredOptions()}>
                {(option, index) => {
                  return (
                    // TODO: a11y
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                    <div
                      class="flex cursor-pointer flex-row items-center justify-between whitespace-nowrap p-2 text-black"
                      classList={{
                        "bg-primary-200": index() === activeOptionIndex(),
                      }}
                      onMouseEnter={() => {
                        setActiveOptionIndex(index());
                      }}
                      onClick={() => valueChanged(option.value)}
                      title={option.name}
                    >
                      <span
                        class="inline-block w-[calc(100%-30px)] truncate"
                        title={option.name}
                      >
                        {option.name}
                      </span>
                      <Show
                        when={unwrap(selectedValues()).includes(option.value)}
                      >
                        <Check class="w-5" />
                      </Show>
                    </div>
                  );
                }}
              </For>
            </div>
          </Portal>
        </Show>
        <Show when={relevantIssues()}>
          <p class="text-sm text-red-500 lg:col-span-2 lg:col-start-2">
            <IssueDisplay issues={relevantIssues()} />
          </p>
        </Show>
        <Show when={!props.hideValidation && getFormErrors() !== false}>
          <p class="text-sm text-error-500 lg:col-span-2 lg:col-start-2">
            <For each={getFormErrors()}>{(error) => <p>{error}</p>}</For>
          </p>
        </Show>
      </Show>
    </div>
  );
}

const ListSelectedOptions: Component<{
  options: ISelectOption[];
  values: SelectValueType[];
  placeholder?: string;
  showAll?: boolean;
  displayType?: "text" | "pill";
}> = (props) => {
  const displayAsPill = () => {
    return props.displayType === "pill";
  };

  const options = createMemo(() =>
    props.options
      .filter((option) => props.values.includes(option.value))
      .slice(0, props.showAll ? props.options.length : 2)
      .map((option) => option.name),
  );

  return (
    <div class="flex w-full flex-row gap-2 overflow-hidden">
      <Show
        when={props.values?.length > 0}
        fallback={<span class="w-full truncate">{props.placeholder}</span>}
      >
        <span
          class={`w-full`}
          classList={{
            "truncate space-x-2 space-y-2": !displayAsPill(),
            "flex flex-wrap gap-2": displayAsPill(),
          }}
        >
          <Show when={displayAsPill()} fallback={<>{options().join(", ")}</>}>
            <For each={options()}>
              {(name) => {
                return <Pill icon={false}>{name}</Pill>;
              }}
            </For>
          </Show>
        </span>
        <Show when={props.values.length > 2 && !props.showAll}>
          +{props.values.length - 2} more
        </Show>
      </Show>
    </div>
  );
};

export default Combobox;
